import { z } from 'zod';

/** Admin read/export DTOs — docs/02_OPENAPI.yaml. Phase 1 is read-only. */

/**
 * One page of the admin check-in list.
 *
 * 100 rather than 50: after migration 0005 the query is an index walk with no
 * sort, so the cost of a page is close to linear in the rows returned, and a
 * densified list shows around 20 of them at a time — a 50-row page ran out
 * after two screens of scrolling. Deliberately not the 200 maximum, because
 * this same default serves the search box, which re-queries on a 300ms
 * debounce over venue Wi-Fi; 200 rows of JSON per keystroke buys nothing for
 * a list nobody reads 200 rows of in one go. The maximum stays available for
 * a caller that knows it wants more.
 */
export const ADMIN_CHECKIN_PAGE_DEFAULT_LIMIT = 100;
export const ADMIN_CHECKIN_PAGE_MAX_LIMIT = 200;

export const AdminCheckinQuerySchema = z.strictObject({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(ADMIN_CHECKIN_PAGE_MAX_LIMIT)
    .default(ADMIN_CHECKIN_PAGE_DEFAULT_LIMIT),
  /** Descending `checkins.id` from the previous page — never an OFFSET. */
  cursor: z.coerce.number().int().min(1).optional(),
  location_id: z.string().max(100).optional(),
  q: z.string().min(1).max(100).optional(),
});

export const LocationCountSchema = z.strictObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  count: z.int().min(0),
  /** The dashboard hides the breakdown when the only location is this one. */
  is_placeholder: z.boolean(),
});

export const AdminSummaryResponseSchema = z.strictObject({
  event_id: z.string(),
  persisted_total: z.int().min(0),
  /** How many of those check-ins also have a departure recorded. */
  checked_out_total: z.int().min(0),
  locations: z.array(LocationCountSchema),
  updated_at: z.iso.datetime({ offset: true }),
});

export const AdminCheckinSchema = z.strictObject({
  id: z.int(),
  submission_id: z.uuid(),
  event_id: z.string(),
  location_id: z.string(),
  location_code: z.string(),
  location_name: z.string(),
  full_name: z.string(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  client_created_at: z.iso.datetime({ offset: true }),
  received_at: z.iso.datetime({ offset: true }),
  persisted_at: z.iso.datetime({ offset: true }),
  /**
   * When the departure reached D1, or null when nobody checked out.
   *
   * Reads from the same clock as `persisted_at` — both are the moment the row
   * landed in D1 — because the number a reader actually wants is the gap
   * between the two, and two columns from two different clocks make that gap
   * meaningless. The participant's own `client_checked_out_at` is in the CSV
   * export for anyone computing dwell time precisely.
   */
  checked_out_at: z.iso.datetime({ offset: true }).nullable(),
});

export const AdminCheckinPageSchema = z.strictObject({
  items: z.array(AdminCheckinSchema),
  next_cursor: z.int().nullable(),
});

export type AdminCheckinQuery = z.infer<typeof AdminCheckinQuerySchema>;
export type LocationCount = z.infer<typeof LocationCountSchema>;
export type AdminSummaryResponse = z.infer<typeof AdminSummaryResponseSchema>;
export type AdminCheckin = z.infer<typeof AdminCheckinSchema>;
export type AdminCheckinPage = z.infer<typeof AdminCheckinPageSchema>;
